import React from 'react';
import PolicyWrapper from '../components/PolicyWrapper';
import { AlertTriangle, Clock } from 'lucide-react';

const TermsOfService = () => {
  return (
    <PolicyWrapper title="Terms of" subtitle="Service">
      <p className="mt-4 text-gray-700 mb-10 text-center">
        Please read these terms carefully before using our website or services.
      </p>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Acceptance of Terms</h2>
        <p className="text-gray-800">
          By accessing and using The Taco Garage website and services, you accept and agree to be bound by 
          the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
        </p>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Product Usage and Safety</h2>
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="text-red-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <p className="text-gray-800 mb-2">
              <strong>Important:</strong> All products sold by The Taco Garage are designed for off-road or aftermarket use.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Products may affect vehicle warranty and safety systems</li>
              <li>Installation should be performed by qualified professionals</li>
              <li>Compliance with local laws and regulations is the customer's responsibility</li>
              <li>Some modifications may not be street legal in all jurisdictions</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Intellectual Property</h2>
        <p className="text-gray-800 mb-4">
          All content on this website, including but not limited to text, graphics, logos, images, 
          and software, is the property of The Taco Garage and is protected by copyright laws.
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li>You may not reproduce, distribute, or create derivative works without permission</li>
          <li>Product images and descriptions are for informational purposes only</li>
          <li>Unauthorized use of our content may result in legal action</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Orders and Availability</h2>
        <div className="flex items-start gap-3 mb-4">
          <Clock className="text-red-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <p className="text-gray-800 mb-2">
              All orders are subject to stock availability and current pricing.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-800">
              <li>Prices are subject to change without notice</li>
              <li>We reserve the right to cancel orders for out-of-stock items</li>
              <li>Shipping times are estimates and may vary</li>
              <li>International shipping may have additional restrictions</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Installation Services</h2>
        <p className="text-gray-800 mb-4">
          Installation services are provided at our Chula Vista location and are subject to scheduling availability.
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li>Appointments must be scheduled in advance</li>
          <li>Installation times are estimates and may vary</li>
          <li>Customer must provide vehicle and any required tools/equipment</li>
          <li>Warranty on installation work is separate from product warranty</li>
          <li>We reserve the right to refuse service for safety or technical reasons</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Returns and Refunds</h2>
        <p className="text-gray-800 mb-4">
          We want you to be satisfied with your purchase. Our return policy includes:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li>30-day return window for unused items in original packaging</li>
          <li>Return shipping costs are the responsibility of the customer</li>
          <li>Custom or special-order items may not be returnable</li>
          <li>Installation services are non-refundable once work begins</li>
          <li>Damaged or defective items will be replaced or refunded</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Limitation of Liability</h2>
        <p className="text-gray-800 mb-4">
          The Taco Garage shall not be liable for any indirect, incidental, special, consequential, 
          or punitive damages resulting from your use of our products or services.
        </p>
        <ul className="list-disc pl-6 space-y-1 text-gray-800">
          <li>We are not responsible for vehicle damage during installation</li>
          <li>Product warranties are provided by manufacturers</li>
          <li>We recommend professional installation for all products</li>
          <li>Customer assumes responsibility for proper use and installation</li>
        </ul>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Privacy and Data</h2>
        <p className="text-gray-800">
          Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
          use, and protect your information.
        </p>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Changes to Terms</h2>
        <p className="text-gray-800">
          We reserve the right to modify these terms at any time. Changes will be effective immediately 
          upon posting on the website. Your continued use of our services constitutes acceptance of the updated terms.
        </p>
      </div>

      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900">Contact Information</h2>
        <p className="text-gray-800 mb-4">
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-800">Taco Garage</p>
          <p className="text-gray-800">1409 Broadway, Suite 101</p>
          <p className="text-gray-800">Chula Vista, CA 91911</p>
          <p className="text-red-600">info@tacogarage.com</p>
        </div>
      </div>

      <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-800">
          <strong>Last updated:</strong> {new Date().toLocaleDateString()}
        </p>
      </div>
    </PolicyWrapper>
  );
};

export default TermsOfService;
